import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, Star } from "lucide-react";
import { api } from "@/lib/api";
import CourseCard from "@/components/CourseCard";
import { CATEGORIES } from "@/lib/categories";

const HERO_IMG = "https://images.unsplash.com/photo-1656390239119-9907645cd479?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjYXIlMjBkcml2aW5nJTIwaGFuZHMlMjBvbiUyMHN0ZWVyaW5nJTIwd2hlZWx8ZW58MHx8fHwxNzg0NDk5NTYwfDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/courses").then(({ data }) => setFeatured(data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative -mt-16 min-h-screen flex items-end overflow-hidden bg-[color:var(--ek-black)]">
        <img
          src={HERO_IMG}
          alt="Modern sürüş eğitimi"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/20 to-black" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-24 pt-40 w-full">
          <div className="overline text-[color:var(--ek-accent)] mb-6 fade-up">Yeni Dönem Kayıtları Başladı</div>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] text-white max-w-4xl fade-up stagger-1">
            Bir belge değil,
            <br />
            <span className="text-[color:var(--ek-accent)]">bir başlangıç.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-neutral-200 max-w-xl leading-relaxed fade-up stagger-2">
            25 yıllık tecrübe, MEB & MYK akreditasyonu ve son teknoloji araçlarla ehliyetten iş makinalarına.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4 fade-up stagger-3">
            <Link to="/basvuru" data-testid="hero-apply-btn" className="btn-accent">
              Hemen Başvur <ArrowRight size={16} />
            </Link>
            <Link to="/kurslar" data-testid="hero-courses-btn" className="btn-ghost !border-white/40 !text-white hover:!bg-white/10">
              Kursları Keşfet
            </Link>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="py-10 bg-[color:var(--ek-surface-light)] border-y border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: <ShieldCheck size={22} />, label: "MEB Onaylı" },
            { icon: <Award size={22} />, label: "MYK Akreditasyonu" },
            { icon: <Star size={22} />, label: "50.000+ Mezun" },
            { icon: <ArrowRight size={22} />, label: "AB Uyumlu Belge" },
          ].map((it, i) => (
            <div key={i} className="flex items-center gap-3 text-neutral-700">
              <span className="text-[color:var(--ek-accent)]">{it.icon}</span>
              <span className="text-sm md:text-base font-medium">{it.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-16">
          <div className="overline text-[color:var(--ek-accent)] mb-4">Kurs Kategorileri</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] text-neutral-900">
            Üç ana alanda uzmanlığa uzanan yolunuz.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CATEGORIES.map((cat, i) => (
            <Link
              key={cat.id}
              to={`/kurslar?kategori=${cat.id}`}
              data-testid={`home-category-${cat.id}`}
              className="group relative bg-neutral-950 text-white rounded-3xl overflow-hidden p-8 md:p-10 aspect-[4/5] flex flex-col justify-between hover:scale-[1.01] transition-transform"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.4) 0%, rgba(10,10,10,0.95) 90%), url(${
                  ["https://images.unsplash.com/photo-1604432264352-54fdecccf881?w=1200",
                   "https://images.unsplash.com/photo-1638262052640-82e94d64664a?w=1200",
                   "https://images.unsplash.com/photo-1740914994657-f1cdffdc418e?w=1200"][i]
                })`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="overline text-[color:var(--ek-accent)]">{cat.subtitle}</div>
              <div>
                <h3 className="font-display text-4xl md:text-5xl tracking-tight leading-none mb-4">{cat.label}</h3>
                <p className="text-neutral-300 text-sm leading-relaxed mb-6 max-w-xs">{cat.description}</p>
                <span className="inline-flex items-center gap-2 text-sm font-medium">
                  Kursları Gör <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured courses */}
      <section className="py-24 bg-[color:var(--ek-surface-light)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <div className="overline text-[color:var(--ek-accent)] mb-4">Öne Çıkanlar</div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight max-w-xl">
                En çok tercih edilen kurslar.
              </h2>
            </div>
            <Link to="/kurslar" data-testid="home-view-all-courses" className="btn-ghost self-start md:self-end">
              Tümünü Gör <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((c, i) => (
              <CourseCard key={c.id} course={c} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[color:var(--ek-black)] text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <div className="overline text-[color:var(--ek-accent)] mb-6">Kayıt İçin Son 3 Kontenjan</div>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.05] mb-6">
            Bugün başvur,
            <br />
            yarın direksiyona geç.
          </h2>
          <p className="text-neutral-400 text-lg mb-10 max-w-xl mx-auto">
            Sana en uygun kursu seç, formu doldur — 24 saat içinde uzman danışmanımız arasın.
          </p>
          <Link to="/basvuru" data-testid="cta-apply-btn" className="btn-accent">
            Ücretsiz Başvuru Yap <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
